// ─────────────────────────────────────────────────────────────────
// Control Systems — Nyquist Criterion
// ─────────────────────────────────────────────────────────────────

const S = {
  wrapper:      { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0 ? S.tdE : S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
      <polygon points="0 0,7 3,0 6" fill={color}/>
    </marker>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared: evaluate G(jw) = num(jw)/den(jw) for a generic TF
// poles/zeros given as real values (real poles on negative real axis)
// ─────────────────────────────────────────────────────────────────
function evalTF(K, zeros, poles, integrators, w) {
  // Returns {re, im} of K * prod(jw - z_i) / (jw^n * prod(jw - p_i))
  // For real poles/zeros: zero at -a means factor (jw + a), pole at -b means 1/(jw+b)
  let re = K, im = 0;
  // Apply zeros (jw + a) where a = -zero_location
  for (const z of zeros) {
    const nr = re * (z) - im * w;
    const ni = re * w + im * (z);
    re = nr; im = ni;
  }
  // Apply poles (jw + b)
  for (const p of poles) {
    const dr = re * (p) - im * w;
    const di = re * w + im * (p);
    const denom = (p * p + w * w);
    re = (dr * p + di * w) / denom;
    im = (di * p - dr * w) / denom;
  }
  // Apply integrators: divide by (jw)^n = (j^n)(w^n)
  for (let n = 0; n < integrators; n++) {
    // divide by jw: multiply by -j/w = (re*0 + im*1)/w, (-re)/w
    if (Math.abs(w) > 1e-10) {
      const nr = im / w;
      const ni = -re / w;
      re = nr; im = ni;
    }
  }
  return { re, im };
}

// Build Nyquist path: positive jω axis (ω: 0+ to ∞)
function buildNyquistPositive(K, zeros, poles, integrators, wMax, N) {
  const pts = [];
  for (let i = 0; i <= N; i++) {
    const w = (i / N) ** 1.5 * wMax; // denser near origin
    if (w < 1e-6) continue;
    const g = evalTF(K, zeros, poles, integrators, w);
    pts.push({ w, re: g.re, im: g.im });
  }
  return pts;
}

// Mirror: negative jω axis is complex conjugate
function mirrorNyquist(pts) {
  return pts.slice().reverse().map(p => ({ w: -p.w, re: p.re, im: -p.im }));
}

// Scale helpers for Nyquist plot
function makeNyquistScalers(ox, oy, W, H, reMin, reMax, imMax) {
  const PW = W, PH = H;
  const imMin = -imMax;
  const sx = re => ox + PW / 2 + (re - (reMin + reMax) / 2) / ((reMax - reMin) / 2) * (PW / 2 - 20);
  const sy = im => oy + PH / 2 - im / imMax * (PH / 2 - 16);
  return { sx, sy };
}

// ─────────────────────────────────────────────────────────────────
// Diagram 1 — Nyquist Contour (D-contour) in the s-plane
// ─────────────────────────────────────────────────────────────────
function DiagramNyquistContour() {
  const W = 900, H = 390;

  // Arc points for the big semicircle (R→∞) on the right
  const bigArcPts = [];
  for (let i = 0; i <= 80; i++) {
    const theta = -Math.PI / 2 + i * Math.PI / 80;
    bigArcPts.push({ x: Math.cos(theta), y: Math.sin(theta) });
  }

  // Small indent arc around origin (ε→0, going clockwise around any imaginary-axis poles)
  const smallArcPts = [];
  for (let i = 0; i <= 40; i++) {
    const theta = Math.PI / 2 - i * Math.PI / 40;
    smallArcPts.push({ x: 0.08 * Math.cos(theta), y: 0.08 * Math.sin(theta) });
  }

  // s-plane center
  const cx = 280, cy = 195, sc = 140;
  const px = x => cx + x * sc;
  const py = y => cy - y * sc;

  const bigArcPath = bigArcPts.map(({ x, y }, i) =>
    `${i === 0 ? 'M' : 'L'}${px(x).toFixed(1)},${py(y).toFixed(1)}`).join(' ');
  const smallArcPath = smallArcPts.map(({ x, y }, i) =>
    `${i === 0 ? 'M' : 'L'}${px(x).toFixed(1)},${py(y).toFixed(1)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Nyquist Contour (D-contour) — Enclosing the Right Half-Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="nc-blk" color="#555"/>
          <Arr id="nc-red" color="#c0392b"/>
          <Arr id="nc-blu" color="#2E75B6"/>
          <Arr id="nc-grn" color="#27ae60"/>
          <Arr id="nc-orn" color="#e07b00"/>
        </defs>

        {/* s-plane panel */}
        <rect x={10} y={10} width={530} height={370} rx={5} fill="#fafcff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={275} y={28} textAnchor="middle" fontSize={13} fill="#7B0D0D" fontWeight="700">s-plane: Nyquist (D) Contour</text>

        {/* RHP shading */}
        <rect x={cx} y={py(1.05)} width={px(1.05) - cx} height={py(-1.05) - py(1.05)} fill="#c0392b" opacity="0.06"/>
        <text x={cx + 30} y={py(0.9)} fontSize={10} fill="#c0392b" opacity="0.7" fontStyle="italic">Right Half-Plane (RHP)</text>
        <text x={cx - 100} y={py(0.9)} fontSize={10} fill="#27ae60" opacity="0.7" fontStyle="italic">Left Half-Plane (LHP)</text>

        {/* Grid */}
        {[-1, 1].map(v => (
          <g key={v}>
            <line x1={px(v)} y1={py(-1.1)} x2={px(v)} y2={py(1.1)} stroke="#eee" strokeWidth="1"/>
            <line x1={px(-1.1)} y1={py(v)} x2={px(1.1)} y2={py(v)} stroke="#eee" strokeWidth="1"/>
            <text x={px(v)} y={cy + 14} textAnchor="middle" fontSize={9} fill="#bbb">{v}</text>
            <text x={cx - 12} y={py(v) + 3} textAnchor="end" fontSize={9} fill="#bbb">{v > 0 ? `j${v}` : `\u2212j${Math.abs(v)}`}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={px(-1.15)} y1={cy} x2={px(1.15)} y2={cy} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#nc-blk)"/>
        <line x1={cx} y1={py(-1.15)} x2={cx} y2={py(1.15)} stroke="#aaa" strokeWidth="1.5" markerEnd="url(#nc-blk)"/>
        <text x={px(1.18)} y={cy + 4} fontSize={10} fill="#888">{'\u03c3'}</text>
        <text x={cx + 5} y={py(1.18)} fontSize={10} fill="#888">j\u03c9</text>

        {/* Segment A: positive imaginary axis (ε to R) */}
        <line x1={cx} y1={py(0.09)} x2={cx} y2={py(1.02)} stroke="#c0392b" strokeWidth="3" markerEnd="url(#nc-red)"/>
        <text x={cx - 14} y={py(0.55)} textAnchor="end" fontSize={10} fill="#c0392b" fontWeight="700">A</text>
        <text x={cx - 14} y={py(0.4)} textAnchor="end" fontSize={8.5} fill="#c0392b">{'\u03c9'}: 0\u207a\u2192\u221e</text>

        {/* Segment B: large semicircle */}
        <path d={bigArcPath} fill="none" stroke="#2E75B6" strokeWidth="3"/>
        {/* Arrow mid-arc at theta=0 (rightmost point) */}
        <line x1={px(1)} y1={py(0.04)} x2={px(1)} y2={py(-0.04)} stroke="#2E75B6" strokeWidth="3" markerEnd="url(#nc-blu)"/>
        <text x={px(1.12)} y={cy - 20} fontSize={10} fill="#2E75B6" fontWeight="700">B</text>
        <text x={px(1.12)} y={cy - 8} fontSize={8.5} fill="#2E75B6">R\u2192\u221e</text>

        {/* Segment C: negative imaginary axis (-R to -ε) */}
        <line x1={cx} y1={py(-1.02)} x2={cx} y2={py(-0.09)} stroke="#27ae60" strokeWidth="3" markerEnd="url(#nc-grn)"/>
        <text x={cx - 14} y={py(-0.5)} textAnchor="end" fontSize={10} fill="#27ae60" fontWeight="700">C</text>
        <text x={cx - 14} y={py(-0.64)} textAnchor="end" fontSize={8.5} fill="#27ae60">{'\u03c9'}: \u2212\u221e\u21920\u207b</text>

        {/* Segment D: small arc around origin (indentation) */}
        <path d={smallArcPath} fill="none" stroke="#e07b00" strokeWidth="2.5"/>
        <text x={px(0.14)} y={cy + 22} fontSize={10} fill="#e07b00" fontWeight="700">D</text>
        <text x={px(0.14)} y={cy + 34} fontSize={8.5} fill="#e07b00">indent. {'\u03b5'}\u21920</text>

        {/* Direction arrows along A and C */}
        <polygon points={`${cx-4},${py(0.72)} ${cx+4},${py(0.72)} ${cx},${py(0.65)}`} fill="#c0392b"/>
        <polygon points={`${cx-4},${py(-0.72)} ${cx+4},${py(-0.72)} ${cx},${py(-0.65)}`} fill="#27ae60" transform={`rotate(180,${cx},${py(-0.685)})`}/>

        {/* CW direction indicator */}
        <path d="M 260,50 A 30,30 0 0 0 220,80" fill="none" stroke="#9b59b6" strokeWidth="2" markerEnd="url(#nc-blk)"/>
        <text x={230} y={46} fontSize={9.5} fill="#9b59b6" fontWeight="700">CW direction</text>

        {/* Labels for axes */}
        <text x={cx} y={py(-1.15) + 14} textAnchor="middle" fontSize={9} fill="#aaa">j\u03c9 axis</text>

        {/* ── RIGHT: explanation ── */}
        <rect x={552} y={10} width={338} height={370} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={721} y={28} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">D-Contour Properties</text>

        {[
          { y: 38, fill: '#FEF0F0', bc: '#c0392b', title: 'Segment A — positive jω axis',
            lines: ['s = j\u03c9, \u03c9: 0\u207a to +\u221e', 'G(j\u03c9) traces upper Nyquist plot', 'Indentation skips any poles at s=0'] },
          { y: 102, fill: '#eaf3ff', bc: '#2E75B6', title: 'Segment B — semicircle R\u2192\u221e',
            lines: ['s = Re^{j\u03b8}, \u03b8: \u2212\u03c0/2 to +\u03c0/2 (CW)', 'For proper G(s): maps to origin', 'For improper: maps to \u221e (unwrapping needed)'] },
          { y: 166, fill: '#f0fff4', bc: '#27ae60', title: 'Segment C — negative jω axis',
            lines: ['s = j\u03c9, \u03c9: \u2212\u221e to 0\u207b', 'G(j\u03c9)* = complex conjugate of A', 'Mirror image of upper plot about real axis'] },
          { y: 230, fill: '#fff5e6', bc: '#e07b00', title: 'Segment D — indentation (if poles on j\u03c9 axis)',
            lines: ['s = \u03b5e^{j\u03b8}, \u03b8: +\u03c0/2 to \u2212\u03c0/2 (CW)', 'Integrators: maps to semicircle at \u221e', 'Needed to avoid passing through poles'] },
          { y: 294, fill: '#f5d0ff', bc: '#9b59b6', title: 'Nyquist theorem (Cauchy arg. principle)',
            lines: ['N = Z \u2212 P', 'N: CW encirclements of \u22121 by G(D)', 'Z: CL poles in RHP, P: OL poles in RHP', 'Stable \u2194 N = \u2212P (counterCW = P times)'] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={560} y={y} width={322} height={lines.length * 18 + 22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={568} y={y + 15} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={568} y={y + 30 + j * 17} fontSize={9} fill="#333">{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The Nyquist contour (D-contour) is a closed path in the s-plane that encircles the entire right half-plane clockwise: up the positive imaginary axis (A), around the large semicircle (B), down the negative imaginary axis (C), and if needed a small indent around any jω-axis poles (D). By Cauchy's argument principle, the number of clockwise encirclements of −1 by the image G(s) as s traverses this contour equals Z−P, where Z and P are the closed-loop and open-loop RHP poles respectively.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 2 — Nyquist Plot Construction (mapping)
// ─────────────────────────────────────────────────────────────────
function DiagramNyquistConstruction() {
  const W = 900, H = 390;

  // G(s) = 2 / ((s+1)(s+2))  — type 0, stable, simple
  // At jω: G(jω) = 2 / ((jω+1)(jω+2)) = 2 / ((1-2ω²)+j3ω)
  const N = 300;
  const wMax = 20;

  const posWs = [];
  for (let i = 0; i <= N; i++) {
    const w = (i / N) ** 1.2 * wMax;
    if (w < 1e-6) { posWs.push({ w: 0.001, re: 2 / 2, im: 0 }); continue; }
    const re = 2 * (1 - 2 * w * w) / ((1 - 2 * w * w) ** 2 + (3 * w) ** 2);
    const im = -2 * 3 * w / ((1 - 2 * w * w) ** 2 + (3 * w) ** 2);
    posWs.push({ w, re, im });
  }
  const negWs = mirrorNyquist(posWs);

  // DC value G(0) = 2/(1*2) = 1
  // High-ω: G→0

  // Scale for Nyquist plot (left panel)
  const nOx = 20, nOy = 30, nPW = 390, nPH = 320;
  const cxN = nOx + nPW / 2, cyN = nOy + nPH / 2;
  const scale = 130;
  const snx = re => cxN + re * scale;
  const sny = im => cyN - im * scale;

  const posPath = posWs.map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
  const negPath = negWs.map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');

  // Key points for annotation
  const ptW05 = posWs.find(p => Math.abs(p.w - 0.5) < 0.02) || posWs[15];
  const ptW1  = posWs.find(p => Math.abs(p.w - 1.0) < 0.02) || posWs[30];
  const ptW2  = posWs.find(p => Math.abs(p.w - 2.0) < 0.03) || posWs[50];

  // Bode for right panel
  const bOx = 428, bOy = 30, bPW = 458, bPH = 140;
  const bWmin = 0.05, bWmax = 50, bNpts = 200;
  const bWs = [];
  for (let i = 0; i <= bNpts; i++) bWs.push(bWmin * Math.pow(bWmax / bWmin, i / bNpts));

  const bMag = bWs.map(w => 20 * Math.log10(2 / (Math.sqrt(1 + w * w) * Math.sqrt(4 + w * w))));
  const bPhs = bWs.map(w => -(Math.atan(w) + Math.atan(w / 2)) * 180 / Math.PI);

  const bswx = w  => bOx + 36 + (Math.log10(w / bWmin) / Math.log10(bWmax / bWmin)) * (bPW - 50);
  const bsmy = dB => bOy + bPH - 18 - ((dB + 40) / 60) * (bPH - 28);
  const bspy = ph => bOy + bPH + 18 + (bPH - 18) - ((ph + 200) / 200) * (bPH - 28);

  const bMagPath = bMag.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${bswx(bWs[i]).toFixed(1)},${bsmy(v).toFixed(1)}`).join(' ');
  const bPhsPath = bPhs.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${bswx(bWs[i]).toFixed(1)},${bspy(v).toFixed(1)}`).join(' ');

  const bwTicks = [0.1, 0.5, 1, 2, 5, 10, 50];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Nyquist Plot Construction — G(s) = 2 / ((s+1)(s+2))</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="npc-blk" color="#aaa"/>
          <Arr id="npc-red" color="#c0392b"/>
          <Arr id="npc-blu" color="#2E75B6"/>
          <clipPath id="npc-nq"><rect x={nOx + 18} y={nOy + 8} width={nPW - 28} height={nPH - 16}/></clipPath>
          <clipPath id="npc-bm"><rect x={bOx + 34} y={bOy + 4} width={bPW - 46} height={bPH - 20}/></clipPath>
          <clipPath id="npc-bp"><rect x={bOx + 34} y={bOy + bPH + 22} width={bPW - 46} height={bPH - 20}/></clipPath>
        </defs>

        {/* ── LEFT: Nyquist plot ── */}
        <rect x={nOx} y={nOy} width={nPW} height={nPH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={nOx + nPW / 2} y={nOy - 8} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Nyquist Plot (G(j\u03c9) as \u03c9 sweeps \u2212\u221e\u2192\u221e)</text>

        {/* Grid */}
        {[-1, -0.5, 0, 0.5, 1].map(v => (
          <g key={v}>
            <line x1={snx(v)} y1={nOy + 8} x2={snx(v)} y2={nOy + nPH - 8} stroke="#f5f5f5" strokeWidth="1"/>
            <line x1={nOx + 10} y1={sny(v)} x2={nOx + nPW - 10} y2={sny(v)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={snx(v)} y={cyN + 14} textAnchor="middle" fontSize={8.5} fill="#ccc">{v}</text>
            {v !== 0 && <text x={cxN - 8} y={sny(v) + 3} textAnchor="end" fontSize={8.5} fill="#ccc">{v > 0 ? `j${v}` : `\u2212j${Math.abs(v)}`}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={nOx + 10} y1={cyN} x2={nOx + nPW - 6} y2={cyN} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#npc-blk)"/>
        <line x1={cxN}      y1={nOy + 8} x2={cxN} y2={nOy + nPH - 6} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#npc-blk)"/>
        <text x={nOx + nPW - 4} y={cyN + 4} fontSize={9} fill="#aaa">Re</text>
        <text x={cxN + 5}       y={nOy + 12} fontSize={9} fill="#aaa">Im</text>

        {/* Unit circle reference */}
        <circle cx={cxN} cy={cyN} r={scale} fill="none" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="4,3"/>

        {/* Critical point −1 */}
        <circle cx={snx(-1)} cy={cyN} r={8} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={snx(-1) - 4} y={cyN - 14} fontSize={10} fill="#c0392b" fontWeight="700">−1</text>

        {/* Nyquist curve — positive ω */}
        <path d={posPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#npc-nq)"/>
        {/* Negative ω (mirror) */}
        <path d={negPath} fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="7,4" clipPath="url(#npc-nq)"/>

        {/* Direction arrow mid-curve */}
        {(() => {
          const midIdx = Math.floor(posWs.length * 0.35);
          const p1 = posWs[midIdx], p2 = posWs[midIdx + 3];
          const dx = snx(p2.re) - snx(p1.re), dy = sny(p2.im) - sny(p1.im);
          const angle = Math.atan2(dy, dx) * 180 / Math.PI;
          return (
            <polygon
              points="0,-5 12,0 0,5"
              fill="#2E75B6"
              transform={`translate(${snx(p1.re)},${sny(p1.im)}) rotate(${angle})`}
            />
          );
        })()}

        {/* Annotated frequency markers */}
        {[ptW05, ptW1, ptW2].map(({ re, im, w }, i) => (
          <g key={i}>
            <circle cx={snx(re)} cy={sny(im)} r={4} fill="#e07b00" stroke="#fff" strokeWidth="1.5"/>
            <text x={snx(re) + 6} y={sny(im) - 5} fontSize={9} fill="#e07b00" fontWeight="700">
              {'\u03c9'}={[0.5, 1, 2][i]}
            </text>
          </g>
        ))}

        {/* DC point G(0) = 1 */}
        <circle cx={snx(1)} cy={cyN} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <text x={snx(1) + 6} y={cyN - 8} fontSize={9} fill="#27ae60" fontWeight="700">G(0)=1</text>

        {/* Legend */}
        <line x1={nOx + 14} y1={nOy + nPH - 14} x2={nOx + 32} y2={nOy + nPH - 14} stroke="#2E75B6" strokeWidth="2.5"/>
        <text x={nOx + 36} y={nOy + nPH - 10} fontSize={9} fill="#2E75B6">{'\u03c9'}: 0\u207a\u2192+\u221e</text>
        <line x1={nOx + 130} y1={nOy + nPH - 14} x2={nOx + 148} y2={nOy + nPH - 14} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3"/>
        <text x={nOx + 152} y={nOy + nPH - 10} fontSize={9} fill="#c0392b">{'\u03c9'}: \u2212\u221e\u21920\u207b (mirror)</text>

        <text x={nOx + nPW / 2} y={nOy + nPH - 1} textAnchor="middle" fontSize={8.5} fill="#555">
          No encirclement of \u22121  \u2192  N=0  \u2192  Z=P=0  \u2192  Stable
        </text>

        {/* ── RIGHT: Corresponding Bode ── */}
        <rect x={bOx} y={bOy} width={bPW} height={bPH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={bOx + bPW / 2} y={bOy - 8} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Corresponding Bode Magnitude [dB]</text>

        {bwTicks.map(wv => (
          <g key={wv}>
            <line x1={bswx(wv)} y1={bOy + 4} x2={bswx(wv)} y2={bOy + bPH - 16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={bswx(wv)} y={bOy + bPH - 4} textAnchor="middle" fontSize={8} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-40, -20, 0].map(mv => (
          <line key={mv} x1={bOx + 34} y1={bsmy(mv)} x2={bOx + bPW - 14} y2={bsmy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        <line x1={bOx + 34} y1={bsmy(0)} x2={bOx + bPW - 14} y2={bsmy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={bOx + 34} y1={bOy + bPH - 16} x2={bOx + bPW - 8} y2={bOy + bPH - 16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#npc-blk)"/>
        <line x1={bOx + 34} y1={bOy + bPH - 16} x2={bOx + 34}     y2={bOy + 4}       stroke="#aaa" strokeWidth="1.2" markerEnd="url(#npc-blk)"/>

        {[{mv:-40,lbl:'-40'},{mv:-20,lbl:'-20'},{mv:0,lbl:'0'}].map(({mv,lbl}) => (
          <text key={mv} x={bOx+30} y={bsmy(mv)+3} textAnchor="end" fontSize={8} fill="#ccc">{lbl}</text>
        ))}

        <path d={bMagPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#npc-bm)"/>

        {/* Phase */}
        <rect x={bOx} y={bOy + bPH + 18} width={bPW} height={bPH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={bOx + bPW / 2} y={bOy + bPH + 10} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Phase [°]</text>

        {bwTicks.map(wv => (
          <line key={wv} x1={bswx(wv)} y1={bOy + bPH + 22} x2={bswx(wv)} y2={bOy + 2 * bPH + 2} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-180, -90, 0].map(pv => (
          <g key={pv}>
            <line x1={bOx + 34} y1={bspy(pv)} x2={bOx + bPW - 14} y2={bspy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={bOx + 30} y={bspy(pv) + 3} textAnchor="end" fontSize={8} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={bOx + 34} y1={bspy(-180)} x2={bOx + bPW - 14} y2={bspy(-180)} stroke="#c0392b" strokeWidth="1" strokeDasharray="4,3" opacity="0.5"/>
        <line x1={bOx + 34} y1={bOy + 2 * bPH + 2} x2={bOx + bPW - 8} y2={bOy + 2 * bPH + 2} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#npc-blk)"/>
        <line x1={bOx + 34} y1={bOy + bPH + 22}    x2={bOx + 34}       y2={bOy + 2 * bPH + 2} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#npc-blk)"/>

        <path d={bPhsPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#npc-bp)"/>
        <text x={bOx + 38} y={bOy + 2 * bPH} fontSize={8} fill="#aaa">{'\u03c9'} [rad/s]</text>
      </svg>
      <div style={S.dCaption}>For G(s)=2/((s+1)(s+2)), as ω sweeps from 0 to ∞, G(jω) traces a curve in the complex plane starting at G(0)=1 on the real axis and spiralling into the origin. The negative ω half (dashed red) is the mirror image about the real axis. The −1 point is not encircled, so N=0, confirming Z=P=0: all closed-loop poles are in the LHP and the system is stable. The Bode plots (right) show the same information — magnitude never crosses 0 dB with phase below −180°.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 3 — Encirclement Rule Explained (visual counting)
// ─────────────────────────────────────────────────────────────────
function DiagramEncirclement() {
  const W = 900, H = 380;

  // Three panels: 0 encirclements, 1 CW, 1 CCW
  // Each panel: a closed curve around or not around (−1)

  // Panel 1: curve doesn't encircle −1 (N=0)
  const curve0 = [];
  for (let i = 0; i <= 100; i++) {
    const t = (i / 100) * 2 * Math.PI;
    curve0.push({ re: 0.3 + 0.4 * Math.cos(t), im: 0.35 * Math.sin(t) });
  }

  // Panel 2: curve encircles −1 once CW (N=1)
  const curve1cw = [];
  for (let i = 0; i <= 100; i++) {
    const t = (i / 100) * 2 * Math.PI;
    curve1cw.push({ re: -1 + 0.5 * Math.cos(t), im: 0.5 * Math.sin(t) });
  }

  // Panel 3: curve encircles −1 once CCW (N=−1)
  const curve1ccw = [];
  for (let i = 100; i >= 0; i--) {
    const t = (i / 100) * 2 * Math.PI;
    curve1ccw.push({ re: -1 + 0.5 * Math.cos(t), im: 0.5 * Math.sin(t) });
  }

  const panels = [
    { title: 'N = 0 (no encirclement)', color: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
      curve: curve0, note: 'Curve does not encircle \u22121', verdict: 'Z = P  (stable if P=0)', N: 0 },
    { title: 'N = +1 (1 CW encirclement)', color: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
      curve: curve1cw, note: 'Curve encircles \u22121 once clockwise', verdict: 'Z = P + 1  (unstable if P=0)', N: 1 },
    { title: 'N = \u22121 (1 CCW encirclement)', color: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
      curve: curve1ccw, note: 'Curve encircles \u22121 counter-clockwise', verdict: 'Z = P \u2212 1  (can stabilize if P=1)', N: -1 },
  ];

  const PW = 248, PH = 220;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Encirclement Counting — Clockwise, Counter-Clockwise, and Zero</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          {panels.map((p, i) => (
            <Arr key={i} id={`enc-${i}`} color={p.color}/>
          ))}
          <Arr id="enc-blk" color="#aaa"/>
        </defs>

        {panels.map(({ title, color, bg, bc, curve, note, verdict, N: Nv }, pi) => {
          const ox = 10 + pi * (PW + 8);
          const oy = 10;
          const cx = ox + PW / 2, cy = oy + PH * 0.48;
          const sc = 78;
          const snx = re => cx + re * sc;
          const sny = im => cy - im * sc;

          const curvePath = curve.map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ') + ' Z';

          // Arrow on curve at roughly 90 degrees
          const ai = Math.floor(curve.length * 0.25);
          const p1 = curve[ai], p2 = curve[ai + 2];
          const dx = snx(p2.re) - snx(p1.re), dy = sny(p2.im) - sny(p1.im);
          const angle = Math.atan2(dy, dx) * 180 / Math.PI;

          return (
            <g key={pi}>
              <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill={bg} stroke={bc} strokeWidth="1.5"/>
              <text x={ox + PW / 2} y={oy + 18} textAnchor="middle" fontSize={11} fill={color} fontWeight="700">{title}</text>

              {/* Grid */}
              {[-1, -0.5, 0, 0.5].map(v => (
                <g key={v}>
                  <line x1={snx(v)} y1={oy + 22} x2={snx(v)} y2={oy + PH - 52} stroke="#eee" strokeWidth="0.8"/>
                  <line x1={ox + 12} y1={sny(v)} x2={ox + PW - 12} y2={sny(v)} stroke="#eee" strokeWidth="0.8"/>
                </g>
              ))}

              {/* Axes */}
              <line x1={ox + 12} y1={cy} x2={ox + PW - 8} y2={cy} stroke="#ccc" strokeWidth="1.2" markerEnd="url(#enc-blk)"/>
              <line x1={cx} y1={oy + 22} x2={cx} y2={oy + PH - 52} stroke="#ccc" strokeWidth="1.2" markerEnd="url(#enc-blk)"/>
              <text x={ox + PW - 6} y={cy + 4} fontSize={8} fill="#bbb">Re</text>
              <text x={cx + 4} y={oy + 25} fontSize={8} fill="#bbb">Im</text>

              {/* Curve with fill */}
              <path d={curvePath} fill={color} fillOpacity="0.12" stroke={color} strokeWidth="2.5"/>

              {/* Direction arrow */}
              <polygon
                points="0,-5 12,0 0,5"
                fill={color}
                transform={`translate(${snx(p1.re)},${sny(p1.im)}) rotate(${angle})`}
              />

              {/* Critical point −1 */}
              <circle cx={snx(-1)} cy={cy} r={8} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
              <text x={snx(-1)} y={cy + 3} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">-1</text>

              {/* Ray from -1 to detect crossings */}
              <line x1={snx(-1)} y1={cy} x2={ox + PW - 12} y2={cy} stroke="#e07b00" strokeWidth="1" strokeDasharray="4,3" opacity="0.7"/>

              {/* N value big */}
              <text x={ox + PW - 36} y={oy + 42} fontSize={28} fill={color} fontWeight="700" opacity="0.4">N={Nv}</text>

              {/* Bottom notes */}
              <rect x={ox + 4} y={oy + PH - 52} width={PW - 8} height={48} rx={3} fill="#fff" stroke={bc} strokeWidth="1"/>
              <text x={ox + PW / 2} y={oy + PH - 38} textAnchor="middle" fontSize={9.5} fill={color} fontWeight="700">{note}</text>
              <text x={ox + PW / 2} y={oy + PH - 24} textAnchor="middle" fontSize={9}   fill="#333">{verdict}</text>
              <text x={ox + PW / 2} y={oy + PH - 10} textAnchor="middle" fontSize={8.5} fill="#555">
                {Nv === 0 ? 'No crossing of real axis to right of \u22121' :
                 Nv === 1 ? 'One CW crossing of real axis to right of \u22121' :
                 'One CCW crossing of real axis to right of \u22121'}
              </text>
            </g>
          );
        })}

        {/* Bottom: counting method */}
        <rect x={10} y={242} width={880} height={130} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={450} y={260} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          How to Count Encirclements — The Ray Method
        </text>
        {[
          { x: 18, fill: '#FEF0F0', bc: '#c0392b',
            title: 'Step 1: Draw a ray',
            lines: ['Draw a horizontal ray from \u22121 to the right', 'Count curve crossings of this ray'] },
          { x: 234, fill: '#eaf3ff', bc: '#2E75B6',
            title: 'Step 2: Count crossings',
            lines: ['\u2191 crossing (bottom to top): subtract 1', '\u2193 crossing (top to bottom): add 1'] },
          { x: 450, fill: '#f0fff4', bc: '#27ae60',
            title: 'Step 3: Apply theorem',
            lines: ['N = Z \u2212 P  (CW encirclements)', 'Z = N + P  (CL RHP poles)'] },
          { x: 666, fill: '#fff5e6', bc: '#e07b00',
            title: 'Stability conclusion',
            lines: ['Stable \u2194 Z = 0 \u2194 N = \u2212P', 'Minimum-phase (P=0): need N=0', 'P open-loop RHP poles: need N=\u2212P CCW encirclements'] },
        ].map(({ x, fill, bc, title, lines }) => (
          <g key={x}>
            <rect x={x + 2} y={268} width={210} height={96} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={x + 10} y={284} fontSize={10} fill={bc} fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={x + 10} y={300 + j * 18} fontSize={9.5} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The orange ray from −1 to the right provides a mechanical counting method. Each time the Nyquist curve crosses the ray travelling downward (+1 to the count), and upward (−1). For N=0 (green): no crossings, curve never encircles −1. For N=+1 (red): curve winds clockwise once around −1 — for a stable open-loop plant this means one unstable closed-loop pole. For N=−1 (blue): counterclockwise encirclement — needed to stabilize a plant with one open-loop RHP pole.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 4 — Stable, Marginal, Unstable: Three Nyquist Plots
// ─────────────────────────────────────────────────────────────────
function DiagramStabilityExamples() {
  const W = 900, H = 380;

  // G(s) = K / (s(s+1)(s+2))  with three gains
  // K=1 (stable), K=6 (marginally stable Ku), K=10 (unstable)
  const Kvals = [1, 6, 10];
  const colors = ['#27ae60', '#e07b00', '#c0392b'];
  const labels = ['K=1 (stable)', 'K=6 (marginal)', 'K=10 (unstable)'];

  const N = 400, wMax = 25;

  const allPts = Kvals.map(K => {
    const pts = [];
    for (let i = 1; i <= N; i++) {
      const w = (i / N) ** 1.3 * wMax;
      // G(jω) = K / (jω(jω+1)(jω+2))
      // denom = (jω)(jω+1)(jω+2) = jω(−ω²+3jω+2) = jω(2−ω²) + j²ω·3ω
      // = jω(2−ω²) − 3ω² = −3ω² + jω(2−ω²)
      const dr = -3 * w * w;
      const di = w * (2 - w * w);
      const d2 = dr * dr + di * di;
      const re = K * dr / d2;
      const im = -K * di / d2;
      pts.push({ w, re, im });
    }
    return pts;
  });

  // For each gain compute where the curve crosses the negative real axis
  // Im = 0 → ω(2−ω²) = 0 → ω = √2 ≈ 1.414
  const wCross = Math.sqrt(2);
  const reCross = Kvals.map(K => {
    const dr = -3 * wCross * wCross;
    const di = wCross * (2 - wCross * wCross);
    const d2 = dr * dr + di * di;
    return K * dr / d2;
  });

  // Nyquist plot dimensions
  const PW = 880, PH = 300;
  const ox = 10, oy = 28;
  const cxN = ox + PW / 2, cyN = oy + PH / 2;

  // Compute a sensible scale from the data
  const allRes = allPts.flatMap(pts => pts.map(p => p.re));
  const allIms = allPts.flatMap(pts => pts.map(p => p.im));
  const scale = 60;

  const snx = re => cxN + re * scale;
  const sny = im => cyN - im * scale;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Nyquist Stability Examples — G(s)=K/[s(s+1)(s+2)] at Three Gains</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="se-blk" color="#aaa"/>
          <clipPath id="se-nq"><rect x={ox + 10} y={oy + 6} width={PW - 20} height={PH - 12}/></clipPath>
        </defs>

        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>

        {/* Grid */}
        {[-10, -8, -6, -4, -2, -1, 0, 2, 4, 6].map(v => (
          <g key={v}>
            <line x1={snx(v)} y1={oy + 6} x2={snx(v)} y2={oy + PH - 6} stroke="#f8f8f8" strokeWidth="1"/>
            <text x={snx(v)} y={cyN + 14} textAnchor="middle" fontSize={8} fill="#ccc">{v}</text>
          </g>
        ))}
        {[-3, -2, -1, 0, 1, 2, 3].map(v => (
          <g key={v}>
            <line x1={ox + 10} y1={sny(v)} x2={ox + PW - 10} y2={sny(v)} stroke="#f8f8f8" strokeWidth="1"/>
            {v !== 0 && <text x={cxN - 6} y={sny(v) + 3} textAnchor="end" fontSize={8} fill="#ccc">{v > 0 ? `j${v}` : `-j${Math.abs(v)}`}</text>}
          </g>
        ))}

        {/* Axes */}
        <line x1={ox + 10} y1={cyN} x2={ox + PW - 6} y2={cyN} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#se-blk)"/>
        <line x1={cxN}     y1={oy + 6} x2={cxN} y2={oy + PH - 6} stroke="#aaa" strokeWidth="1.3" markerEnd="url(#se-blk)"/>
        <text x={ox + PW - 4} y={cyN + 4} fontSize={9} fill="#888">Re</text>
        <text x={cxN + 5}     y={oy + 10} fontSize={9} fill="#888">Im</text>

        {/* Critical point −1 */}
        <circle cx={snx(-1)} cy={cyN} r={9} fill="#c0392b" stroke="#fff" strokeWidth="2.5"/>
        <text x={snx(-1)} y={cyN + 3} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">−1</text>
        <text x={snx(-1)} y={cyN - 14} textAnchor="middle" fontSize={9} fill="#c0392b" fontWeight="700">Critical point</text>

        {/* All three Nyquist curves (positive ω only for clarity) */}
        {allPts.map((pts, ki) => {
          const d = pts.map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
          const dmirror = pts.slice().reverse().map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(-im).toFixed(1)}`).join(' ');
          return (
            <g key={ki}>
              <path d={d}       fill="none" stroke={colors[ki]} strokeWidth={2.2} clipPath="url(#se-nq)"/>
              <path d={dmirror} fill="none" stroke={colors[ki]} strokeWidth={1.5} strokeDasharray="6,4" clipPath="url(#se-nq)" opacity="0.7"/>
            </g>
          );
        })}

        {/* Real-axis crossing markers */}
        {reCross.map((re, ki) => (
          <g key={ki}>
            <circle cx={snx(re)} cy={cyN} r={6} fill={colors[ki]} stroke="#fff" strokeWidth="2"/>
            <text x={snx(re)} y={cyN + 20 + ki * 14} textAnchor="middle" fontSize={9} fill={colors[ki]} fontWeight="700">
              {labels[ki]}: cross={re.toFixed(3)}
            </text>
          </g>
        ))}

        {/* Stability verdict boxes */}
        {[
          { x: ox + 20, y: oy + 12, color: '#27ae60', bg: '#f0fff4',
            text: 'K=1: cross=−' + Math.abs(reCross[0]).toFixed(2) + ' > −1  \u2192  No encirclement  \u2192  STABLE' },
          { x: ox + 20, y: oy + 30, color: '#e07b00', bg: '#fff5e6',
            text: 'K=6: cross=−' + Math.abs(reCross[1]).toFixed(2) + ' = −1  \u2192  Passes through \u22121  \u2192  MARGINAL' },
          { x: ox + 20, y: oy + 48, color: '#c0392b', bg: '#FEF0F0',
            text: 'K=10: cross=−' + Math.abs(reCross[2]).toFixed(2) + ' < −1  \u2192  Encircles \u22121 twice  \u2192  UNSTABLE' },
        ].map(({ x, y, color, bg, text }) => (
          <g key={y}>
            <rect x={x} y={y} width={660} height={14} rx={2} fill={bg} stroke={color} strokeWidth="0.7"/>
            <text x={x + 6} y={y + 11} fontSize={9} fill={color} fontWeight="700">{text}</text>
          </g>
        ))}

        {/* Legend */}
        {labels.map((lbl, i) => (
          <g key={i}>
            <line x1={ox + PW - 200 + i * 62} y1={oy + PH - 14} x2={ox + PW - 182 + i * 62} y2={oy + PH - 14} stroke={colors[i]} strokeWidth="2.5"/>
            <text x={ox + PW - 178 + i * 62} y={oy + PH - 10} fontSize={8.5} fill={colors[i]}>{lbl}</text>
          </g>
        ))}

        {/* Bottom bar */}
        <rect x={ox} y={oy + PH + 8} width={PW} height={44} rx={4} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.2"/>
        <text x={ox + PW / 2} y={oy + PH + 22} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">
          Key insight for type-1 systems: stability determined by where Nyquist curve crosses negative real axis
        </text>
        <text x={ox + PW / 2} y={oy + PH + 38} textAnchor="middle" fontSize={10} fill="#555">
          Crossing to the right of \u22121: stable (K{'<'}Ku). Crossing exactly at \u22121: marginal (K=Ku). Crossing to the left: encircles \u22121 and is unstable (K{'<'}\u00b0).
        </text>
      </svg>
      <div style={S.dCaption}>The three-pole integrating plant G(s)=K/[s(s+1)(s+2)] shows how gain shifts the Nyquist curve. The curve always crosses the negative real axis at ω=√2 rad/s, but the crossing value is K/6. For K=1: crosses at −1/6 (right of −1, stable). For K=6: crosses exactly at −1 (marginally stable, Ku=6). For K=10: crosses at −10/6≈−1.67 (left of −1), the curve encircles −1 twice clockwise: two unstable closed-loop poles.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 5 — Gain and Phase Margins from Nyquist Plot
// ─────────────────────────────────────────────────────────────────
function DiagramNyquistMargins() {
  const W = 900, H = 390;

  // G(s) = 3 / (s(s+1)(0.5s+1))  — type 1
  const N = 500, wMax = 30;
  const K = 3;

  const pts = [];
  for (let i = 1; i <= N; i++) {
    const w = (i / N) ** 1.4 * wMax;
    // G(jω) = 3 / (jω·(jω+1)·(0.5jω+1))
    // Let u=ω: jω = jω, (jω+1) = 1+jω, (0.5jω+1) = 1+0.5jω
    // num = 3, den = jω·(1+jω)·(1+0.5jω)
    // (1+jω)(1+0.5jω) = 1 + 1.5jω - 0.5ω²
    const p1re = 1 - 0.5 * w * w, p1im = 1.5 * w;
    // * jω = -(1.5ω²) + j(ω(1-0.5ω²))
    const dre = -1.5 * w * w;
    const dim = w * (1 - 0.5 * w * w);
    const d2 = dre * dre + dim * dim;
    const re = K * dre / d2;
    const im = -K * dim / d2;
    pts.push({ w, re, im });
  }

  // Phase crossover: Im = 0 → ω(1-0.5ω²)=0 → ω=√2
  const wpc = Math.sqrt(2);
  const drePC = -1.5 * wpc * wpc;
  const d2PC = drePC * drePC;
  const rePC = K * drePC / d2PC;
  const GM_dB = -20 * Math.log10(Math.abs(rePC));

  // Gain crossover: |G(jωgc)| = 1
  let gcIdx = 0;
  for (let i = 1; i < pts.length; i++) {
    const mag = Math.sqrt(pts[i].re ** 2 + pts[i].im ** 2);
    if (mag < 1.0 && Math.sqrt(pts[i - 1].re ** 2 + pts[i - 1].im ** 2) >= 1.0) {
      gcIdx = i;
      break;
    }
  }
  const gcPt = pts[gcIdx] || pts[100];
  const PM = Math.atan2(gcPt.im, gcPt.re) * 180 / Math.PI + 180;

  // Scale
  const ox = 18, oy = 18, PW = 460, PH = 340;
  const cxN = ox + PW * 0.45, cyN = oy + PH / 2;
  const scale = 70;
  const snx = re => cxN + re * scale;
  const sny = im => cyN - im * scale;

  const posPath = pts.map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
  const negPath = pts.slice().reverse().map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(-im).toFixed(1)}`).join(' ');

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Gain Margin &amp; Phase Margin — Geometric Construction on Nyquist Plot</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="nm-blk" color="#aaa"/>
          <Arr id="nm-blu" color="#2E75B6"/>
          <Arr id="nm-grn" color="#27ae60"/>
          <Arr id="nm-red" color="#c0392b"/>
          <clipPath id="nm-nq"><rect x={ox + 10} y={oy + 6} width={PW - 20} height={PH - 12}/></clipPath>
        </defs>

        {/* Nyquist panel */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox + PW / 2} y={oy - 6} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          G(s)=3/[s(s+1)(0.5s+1)]
        </text>

        {/* Grid */}
        {[-3, -2, -1, 0, 1].map(v => (
          <g key={v}>
            <line x1={snx(v)} y1={oy + 6} x2={snx(v)} y2={oy + PH - 6} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={snx(v)} y={cyN + 14} textAnchor="middle" fontSize={8.5} fill="#bbb">{v}</text>
          </g>
        ))}
        {[-2, -1, 0, 1, 2].map(v => (
          <g key={v}>
            <line x1={ox + 10} y1={sny(v)} x2={ox + PW - 10} y2={sny(v)} stroke="#f5f5f5" strokeWidth="1"/>
            {v !== 0 && <text x={cxN - 6} y={sny(v) + 3} textAnchor="end" fontSize={8.5} fill="#bbb">{v > 0 ? `j${v}` : `\u2212j${Math.abs(v)}`}</text>}
          </g>
        ))}

        {/* Unit circle */}
        <circle cx={cxN} cy={cyN} r={scale} fill="none" stroke="#e0e0e0" strokeWidth="1" strokeDasharray="5,4"/>
        <text x={cxN + scale + 4} y={cyN - 4} fontSize={8} fill="#ccc">|G|=1</text>

        {/* Axes */}
        <line x1={ox + 10} y1={cyN} x2={ox + PW - 6} y2={cyN} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#nm-blk)"/>
        <line x1={cxN}     y1={oy + 6} x2={cxN} y2={oy + PH - 6} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#nm-blk)"/>
        <text x={ox + PW - 4} y={cyN + 4} fontSize={9} fill="#888">Re</text>
        <text x={cxN + 5}     y={oy + 12} fontSize={9} fill="#888">Im</text>

        {/* Critical point */}
        <circle cx={snx(-1)} cy={cyN} r={8} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={snx(-1)} y={cyN + 3} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">−1</text>

        {/* Curves */}
        <path d={posPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#nm-nq)"/>
        <path d={negPath} fill="none" stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="6,4" clipPath="url(#nm-nq)" opacity="0.6"/>

        {/* Phase crossover point */}
        <circle cx={snx(rePC)} cy={cyN} r={6} fill="#2E75B6" stroke="#fff" strokeWidth="2"/>
        <text x={snx(rePC)} y={cyN + 20} textAnchor="middle" fontSize={9} fill="#2E75B6" fontWeight="700">PC</text>
        <text x={snx(rePC)} y={cyN + 32} textAnchor="middle" fontSize={8.5} fill="#555">{'\u03c9'}=\u221a2</text>

        {/* GM: distance from PC to (−1,0) */}
        <line x1={snx(rePC)} y1={cyN} x2={snx(-1)} y2={cyN} stroke="#2E75B6" strokeWidth="3" markerStart="url(#nm-blu)" markerEnd="url(#nm-blu)"/>
        <text x={(snx(rePC) + snx(-1)) / 2} y={cyN - 10} textAnchor="middle" fontSize={10} fill="#2E75B6" fontWeight="700">
          GM = 1/|G(j{'\u221a'}2)| = {(1/Math.abs(rePC)).toFixed(2)}\u00d7 = {GM_dB.toFixed(1)} dB
        </text>

        {/* GC point */}
        <circle cx={snx(gcPt.re)} cy={sny(gcPt.im)} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
        <text x={snx(gcPt.re) + 8} y={sny(gcPt.im) - 6} fontSize={9} fill="#27ae60" fontWeight="700">GC</text>

        {/* PM: angle from -180° to the GC angle */}
        {/* Draw arc from (−1,0) direction to the GC direction */}
        <line x1={cxN} y1={cyN} x2={snx(gcPt.re)} y2={sny(gcPt.im)} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        <line x1={cxN} y1={cyN} x2={snx(-1.2)} y2={cyN} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        {/* PM arc */}
        {(() => {
          const r2 = 30;
          const phiGC = Math.atan2(gcPt.im, gcPt.re); // negative angle
          const arcD = [];
          for (let i = 0; i <= 20; i++) {
            const theta = Math.PI + i * (phiGC - Math.PI) / 20;
            arcD.push({ x: cxN + r2 * Math.cos(theta), y: cyN - r2 * Math.sin(theta) });
          }
          return (
            <path
              d={arcD.map(({ x, y }, i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')}
              fill="none" stroke="#27ae60" strokeWidth="2.5"
            />
          );
        })()}
        <text x={cxN - 50} y={cyN - 34} fontSize={10} fill="#27ae60" fontWeight="700">PM = {PM.toFixed(1)}°</text>

        {/* ── RIGHT: Definitions panel ── */}
        <rect x={494} y={oy} width={396} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={692} y={oy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Margins from the Nyquist Plot</text>

        {[
          { y: oy + 28, fill: '#eaf3ff', bc: '#2E75B6', title: 'Gain Margin (GM) — from phase crossover',
            lines: ['Phase crossover: where curve hits negative real axis',
                    'GM = 1/|G(j\u03c9pc)| as a factor',
                    'GM_dB = \u221220·log\u2081\u2080|G(j\u03c9pc)|',
                    `This system: |G(j\u221a2)| = ${Math.abs(rePC).toFixed(3)}`,
                    `GM = 1/${Math.abs(rePC).toFixed(3)} = ${(1/Math.abs(rePC)).toFixed(2)}\u00d7 = ${GM_dB.toFixed(1)} dB`,
                    'Target: GM \u2265 6 dB (factor \u2265 2\u00d7)'] },
          { y: oy + 148, fill: '#f0fff4', bc: '#27ae60', title: 'Phase Margin (PM) — from gain crossover',
            lines: ['Gain crossover: where curve hits unit circle (|G|=1)',
                    'PM = angle between GC point and \u22121 direction',
                    'PM = 180° + \u2220G(j\u03c9gc)',
                    `This system: \u2220G(j\u03c9gc) = ${(PM - 180).toFixed(1)}°`,
                    `PM = ${PM.toFixed(1)}°`,
                    'Target: PM \u2265 45°'] },
          { y: oy + 268, fill: '#FEF0F0', bc: '#c0392b', title: 'Stability interpretation',
            lines: ['Stable: critical point (\u22121) outside curve AND GM>0, PM>0',
                    'CW encirclement: unstable (GM<0 dB or PM<0°)',
                    'Nyquist captures both margins geometrically'] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={502} y={y} width={380} height={lines.length * 17 + 22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={510} y={y + 15} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={510} y={y + 29 + j * 16} fontSize={9} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Gain margin is the factor by which gain can increase before the Nyquist curve passes through −1: it is 1/|G(jωpc)| where ωpc is the phase crossover frequency. Phase margin is the angle between the gain crossover point GC (on the unit circle) and the −180° direction. Both margins are visible simultaneously on a single Nyquist plot, giving an immediate geometric picture of stability robustness.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 6 — Non-Minimum Phase & RHP Open-Loop Poles
// ─────────────────────────────────────────────────────────────────
function DiagramNonMinimumPhase() {
  const W = 900, H = 390;

  // Case 1: RHP zero (non-minimum phase) G(s) = (1-s)/(s+1)(s+2)
  const ptsRHPZ = [];
  for (let i = 0; i <= 300; i++) {
    const w = (i / 300) ** 1.2 * 20;
    if (w < 0.01) continue;
    // G(jω) = (1-jω)/((jω+1)(jω+2))
    const numRe = 1, numIm = -w;
    const d1re = 1, d1im = w; // (jω+1)
    const d2re = 2, d2im = w; // (jω+2)
    const dRe = d1re * d2re - d1im * d2im;
    const dIm = d1re * d2im + d1im * d2re;
    const d2m = dRe * dRe + dIm * dIm;
    const re = (numRe * dRe + numIm * dIm) / d2m;
    const im = (numIm * dRe - numRe * dIm) / d2m;
    ptsRHPZ.push({ w, re, im });
  }

  // Case 2: Minimum phase equivalent G(s) = (1+s)/(s+1)(s+2) = 1/(s+2)
  const ptsMP = [];
  for (let i = 0; i <= 300; i++) {
    const w = (i / 300) ** 1.2 * 20;
    if (w < 0.01) continue;
    const re = 2 / (4 + w * w);
    const im = -w / (4 + w * w);
    ptsMP.push({ w, re, im });
  }

  // Case 3: RHP open-loop pole G(s) = 1/((s-1)(s+2)) — unstable plant
  // For stability: need N = -P = -1 CCW encirclement
  const ptsRHPP = [];
  for (let i = 0; i <= 300; i++) {
    const w = (i / 300) ** 1.2 * 20;
    if (w < 0.01) continue;
    // G(jω) = 1/((jω-1)(jω+2))
    const d1re = -1, d1im = w; // (jω-1)
    const d2re = 2, d2im = w;  // (jω+2)
    const dRe = d1re * d2re - d1im * d2im;
    const dIm = d1re * d2im + d1im * d2re;
    const d2m = dRe * dRe + dIm * dIm;
    const re = dRe / d2m;
    const im = -dIm / d2m;
    ptsRHPP.push({ w, re, im });
  }

  const panels = [
    { title: 'RHP Zero (Non-minimum phase)', subtitle: 'G(s)=(1\u2212s)/((s+1)(s+2))', pts: ptsRHPZ, mirror: mirrorNyquist(ptsRHPZ),
      color: '#c0392b', ox: 10, scale: 120,
      note: ['Same |G| as MP version but extra phase lag', 'Phase wraps below \u2212180\u00b0 at high freq.', 'Harder to stabilize — Bode criterion may fail'] },
    { title: 'Minimum Phase equivalent', subtitle: 'G(s)=1/(s+2)', pts: ptsMP, mirror: mirrorNyquist(ptsMP),
      color: '#27ae60', ox: 310, scale: 120,
      note: ['Standard phase behaviour', 'Phase never exceeds \u2212180\u00b0', 'Bode criterion applies directly'] },
    { title: 'RHP Open-Loop Pole (P=1)', subtitle: 'G(s)=1/((s\u22121)(s+2))', pts: ptsRHPP, mirror: mirrorNyquist(ptsRHPP),
      color: '#2E75B6', ox: 610, scale: 120,
      note: ['P=1 open-loop RHP pole', 'Need N=\u22121 (1 CCW encirclement)', 'For stability: curve must encircle \u22121 CCW once'] },
  ];

  const PW = 280, PH = 270;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Non-Minimum Phase &amp; RHP Open-Loop Poles — Special Nyquist Cases</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="nmp-blk" color="#aaa"/>
          {panels.map((p, i) => <Arr key={i} id={`nmp-${i}`} color={p.color}/>)}
          {panels.map((_, i) => (
            <clipPath key={i} id={`nmp-c${i}`}>
              <rect x={panels[i].ox + 8} y={18} width={PW - 16} height={PH - 12}/>
            </clipPath>
          ))}
        </defs>

        {panels.map(({ title, subtitle, pts, mirror, color, ox, scale, note }, pi) => {
          const cx = ox + PW / 2, cy = 18 + PH / 2;
          const snx = re => cx + re * scale;
          const sny = im => cy - im * scale;

          const posPath = pts.map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
          const negPath = mirror.map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');

          return (
            <g key={pi}>
              <rect x={ox} y={12} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
              <text x={ox + PW / 2} y={7} textAnchor="middle" fontSize={10.5} fill={color} fontWeight="700">{title}</text>

              {/* Axes */}
              {[-1, -0.5, 0, 0.5].map(v => (
                <g key={v}>
                  <line x1={snx(v)} y1={18} x2={snx(v)} y2={18 + PH - 12} stroke="#f5f5f5" strokeWidth="1"/>
                  <line x1={ox + 8} y1={sny(v)} x2={ox + PW - 8} y2={sny(v)} stroke="#f5f5f5" strokeWidth="1"/>
                  <text x={snx(v)} y={cy + 14} textAnchor="middle" fontSize={7.5} fill="#ccc">{v}</text>
                </g>
              ))}
              <line x1={ox + 8} y1={cy} x2={ox + PW - 6} y2={cy} stroke="#bbb" strokeWidth="1.2" markerEnd="url(#nmp-blk)"/>
              <line x1={cx} y1={18} x2={cx} y2={18 + PH - 10} stroke="#bbb" strokeWidth="1.2" markerEnd="url(#nmp-blk)"/>
              <text x={ox + PW - 4} y={cy + 4} fontSize={7.5} fill="#aaa">Re</text>
              <text x={cx + 4} y={24} fontSize={7.5} fill="#aaa">Im</text>

              {/* Unit circle */}
              <circle cx={cx} cy={cy} r={scale} fill="none" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="3,3"/>

              {/* Critical point */}
              <circle cx={snx(-1)} cy={cy} r={7} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
              <text x={snx(-1)} y={cy + 3} textAnchor="middle" fontSize={8} fill="#fff" fontWeight="700">-1</text>

              {/* Curves */}
              <path d={posPath} fill="none" stroke={color} strokeWidth="2.5" clipPath={`url(#nmp-c${pi})`}/>
              <path d={negPath} fill="none" stroke={color} strokeWidth="1.8" strokeDasharray="6,4" clipPath={`url(#nmp-c${pi})`} opacity="0.7"/>

              {/* Subtitle */}
              <text x={ox + PW / 2} y={18 + PH - 6} textAnchor="middle" fontSize={9} fill="#555" fontFamily="'Courier New',monospace">{subtitle}</text>

              {/* Notes */}
              <rect x={ox + 2} y={18 + PH + 4} width={PW - 4} height={note.length * 16 + 10} rx={3} fill={color + '18'} stroke={color} strokeWidth="0.8"/>
              {note.map((l, j) => (
                <text key={j} x={ox + 8} y={18 + PH + 17 + j * 15} fontSize={8.5} fill="#333">▸ {l}</text>
              ))}
            </g>
          );
        })}

        {/* Bottom: key differences */}
        <rect x={10} y={336} width={880} height={48} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.2"/>
        <text x={450} y={352} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Why Nyquist Handles These Cases While Bode Cannot</text>
        <text x={450} y={368} textAnchor="middle" fontSize={9.5} fill="#555">
          Non-minimum phase and RHP open-loop poles require counting encirclements (Cauchy), not just checking margin signs. Bode stability criterion applies ONLY to minimum-phase, stable open-loop systems. Nyquist criterion works universally.
        </text>
        <text x={450} y={380} textAnchor="middle" fontSize={9.5} fill="#c0392b" fontWeight="700">
          Formula: N = Z \u2212 P  \u2192  Z = N + P  |  P = number of open-loop RHP poles  |  Z = number of CL RHP poles
        </text>
      </svg>
      <div style={S.dCaption}>Three special cases requiring the full Nyquist criterion: (left) a non-minimum phase system with an RHP zero has the same magnitude Bode plot as its minimum-phase counterpart but an extra phase lag — Bode criterion cannot distinguish them. (middle) Minimum-phase equivalent for comparison. (right) An unstable open-loop plant (P=1) requires the Nyquist curve to encircle −1 exactly once counterclockwise (N=−1) for closed-loop stability — the Bode plot cannot predict this.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 7 — Nyquist for Systems with Dead Time
// ─────────────────────────────────────────────────────────────────
function DiagramDeadTime() {
  const W = 900, H = 380;

  // G(s) = e^{-Ls} / (s+1)(0.5s+1) for L = 0, 0.5, 1.5
  const Lvals  = [0, 0.5, 1.5];
  const Lcolors = ['#27ae60', '#e07b00', '#c0392b'];

  const N = 400, wMax = 15;

  const allPts = Lvals.map(L => {
    const pts = [];
    for (let i = 1; i <= N; i++) {
      const w = (i / N) ** 1.2 * wMax;
      // |G(jω)| = 1/(sqrt(1+ω²)*sqrt(1+(0.5ω)²)) — dead time doesn't affect magnitude
      const mag = 1 / (Math.sqrt(1 + w * w) * Math.sqrt(1 + (0.5 * w) ** 2));
      // Phase = -(atan(ω) + atan(0.5ω)) - L*ω
      const phase = -(Math.atan(w) + Math.atan(0.5 * w)) - L * w;
      pts.push({ w, re: mag * Math.cos(phase), im: mag * Math.sin(phase) });
    }
    return pts;
  });

  // Scale
  const ox = 18, oy = 18, PW = 460, PH = 330;
  const cxN = ox + PW * 0.42, cyN = oy + PH / 2;
  const scale = 130;
  const snx = re => cxN + re * scale;
  const sny = im => cyN - im * scale;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Dead Time Effect on Nyquist Plot — G(s)=e^(\u2212Ls)/[(s+1)(0.5s+1)]</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="dt-blk" color="#aaa"/>
          <clipPath id="dt-nq"><rect x={ox + 10} y={oy + 6} width={PW - 20} height={PH - 12}/></clipPath>
        </defs>

        {/* Nyquist */}
        <rect x={ox} y={oy} width={PW} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={ox + PW / 2} y={oy - 6} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">
          Nyquist plot: increasing dead time L spirals inward
        </text>

        {/* Grid */}
        {[-1, -0.5, 0, 0.5, 1].map(v => (
          <g key={v}>
            <line x1={snx(v)} y1={oy + 6} x2={snx(v)} y2={oy + PH - 6} stroke="#f5f5f5" strokeWidth="1"/>
            <line x1={ox + 10} y1={sny(v)} x2={ox + PW - 10} y2={sny(v)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={snx(v)} y={cyN + 14} textAnchor="middle" fontSize={8.5} fill="#ccc">{v}</text>
          </g>
        ))}

        {/* Unit circle */}
        <circle cx={cxN} cy={cyN} r={scale} fill="none" stroke="#f0f0f0" strokeWidth="1" strokeDasharray="4,3"/>

        {/* Axes */}
        <line x1={ox + 10} y1={cyN} x2={ox + PW - 6} y2={cyN} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#dt-blk)"/>
        <line x1={cxN}     y1={oy + 6} x2={cxN} y2={oy + PH - 6} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#dt-blk)"/>
        <text x={ox + PW - 4} y={cyN + 4} fontSize={9} fill="#888">Re</text>
        <text x={cxN + 5}     y={oy + 12} fontSize={9} fill="#888">Im</text>

        {/* Critical point */}
        <circle cx={snx(-1)} cy={cyN} r={8} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={snx(-1)} y={cyN + 3} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">−1</text>

        {/* Nyquist curves */}
        {allPts.map((pts, li) => {
          const d    = pts.map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
          const dmr  = pts.slice().reverse().map(({ re, im }, i) =>
            `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(-im).toFixed(1)}`).join(' ');
          return (
            <g key={li}>
              <path d={d}   fill="none" stroke={Lcolors[li]} strokeWidth="2.5" clipPath="url(#dt-nq)"/>
              <path d={dmr} fill="none" stroke={Lcolors[li]} strokeWidth="1.5" strokeDasharray="6,4" clipPath="url(#dt-nq)" opacity="0.6"/>
            </g>
          );
        })}

        {/* Legend */}
        {Lvals.map((L, li) => (
          <g key={li}>
            <line x1={ox + 14} y1={oy + PH - 18 + li * 14} x2={ox + 32} y2={oy + PH - 18 + li * 14} stroke={Lcolors[li]} strokeWidth="2.5"/>
            <text x={ox + 36} y={oy + PH - 14 + li * 14} fontSize={9} fill={Lcolors[li]}>L = {L} s</text>
          </g>
        ))}

        {/* ── RIGHT: Analysis ── */}
        <rect x={492} y={oy} width={398} height={PH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={691} y={oy + 18} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Dead Time: Magnitude vs Phase Effect</text>

        {[
          { y: oy + 28, fill: '#f0fff4', bc: '#27ae60', title: 'L = 0 (no delay)',
            lines: ['Standard minimum-phase Nyquist curve', 'Phase: \u2212(atan(\u03c9)+atan(0.5\u03c9)) \u2192 max \u221290\u2212\u221263 = \u2212153\u00b0', 'Curve spirals inward, stays near real axis', 'Does not cross negative real axis'] },
          { y: oy + 110, fill: '#fff5e6', bc: '#e07b00', title: 'L = 0.5 s (moderate delay)',
            lines: ['Magnitude unchanged: |e^{j\u03b8}| = 1', 'Extra phase: \u2212L\u03c9 radians at each \u03c9', 'Curve spirals more — crosses negative real axis', 'Still stable but reduced phase margin'] },
          { y: oy + 202, fill: '#FEF0F0', bc: '#c0392b', title: 'L = 1.5 s (large delay)',
            lines: ['Massive additional phase rotation', 'Curve winds around \u22121 multiple times', 'Can become unstable', 'Dead time limits achievable bandwidth: \u03c9max \u2248 PM/(L\u00b7180/\u03c0)'] },
        ].map(({ y, fill, bc, title, lines }) => (
          <g key={y}>
            <rect x={500} y={y} width={382} height={lines.length * 17 + 22} rx={3} fill={fill} stroke={bc} strokeWidth="1"/>
            <text x={508} y={y + 15} fontSize={10} fill="#7B0D0D" fontWeight="700">{title}</text>
            {lines.map((l, j) => (
              <text key={j} x={508} y={y + 29 + j * 16} fontSize={9} fill="#333">▸ {l}</text>
            ))}
          </g>
        ))}

        {/* Formula for critical L */}
        <rect x={500} y={oy + 306} width={382} height={22} rx={3} fill="#eaf3ff" stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={691} y={oy + 321} textAnchor="middle" fontSize={10} fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          {"Limiting dead time: L_max = PM (rad) / ωgc"}
        </text>
      </svg>
      <div style={S.dCaption}>Dead time e^(−Ls) does not change the magnitude of G(jω) — the Nyquist curve stays on the same radii — but it adds a phase rotation of −Lω radians that grows with frequency. This causes the curve to spiral inward, potentially crossing through −1 and causing instability. The critical insight from the Nyquist plot is immediate: larger L rotates the curve more, and the spiral can encircle −1. The maximum allowable dead time is L_max ≈ PM/(ωgc) seconds.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Diagram 8 — Nyquist vs Bode: Same System Both Ways
// ─────────────────────────────────────────────────────────────────
function DiagramNyquistVsBode() {
  const W = 900, H = 400;

  // G(s) = 5 / (s(s+1)(s+3))  — type 1, three poles
  const K = 5;
  const N = 500, wMax = 20;
  const Npts = 200, bWmin = 0.05, bWmax = 40;

  // Nyquist
  const nyqPts = [];
  for (let i = 1; i <= N; i++) {
    const w = (i / N) ** 1.3 * wMax;
    // G(jω) = 5 / (jω(jω+1)(jω+3))
    const d1re = 0, d1im = w;  // jω
    const d2re = 1, d2im = w;  // jω+1
    const d3re = 3, d3im = w;  // jω+3
    // d1*d2 = (0+jω)(1+jω) = -ω²+jω
    const r12re = -w * w, r12im = w;
    // *d3 = (-ω²+jω)(3+jω) = -3ω² -ω³j + 3ωj -ω² = -(3ω²+ω²) + j(3ω-ω³)
    // Wait let me be careful:
    // (-ω²)(3) + (-ω²)(jω) + (jω)(3) + (jω)(jω)
    // = -3ω² - jω³ + 3jω - ω²
    // = (-4ω²) + j(3ω - ω³) ... actually:
    // r12 * d3 = (r12re + j·r12im)(3 + jw)
    const dRe = r12re * 3 - r12im * w;
    const dIm = r12re * w + r12im * 3;
    const d2m = dRe * dRe + dIm * dIm;
    const re = K * dRe / d2m;
    const im = -K * dIm / d2m;
    nyqPts.push({ w, re, im });
  }

  // Bode
  const bWs = [];
  for (let i = 0; i <= Npts; i++) bWs.push(bWmin * Math.pow(bWmax / bWmin, i / Npts));

  const bMag = bWs.map(w => 20 * Math.log10(K / (w * Math.sqrt(1 + w * w) * Math.sqrt(9 + w * w))));
  const bPhs = bWs.map(w => (-90 - Math.atan(w) - Math.atan(w / 3)) * 180 / Math.PI);

  // Find phase crossover for Bode: phase = -180
  let bPcIdx = 0;
  for (let i = 1; i < bPhs.length; i++) {
    if (bPhs[i - 1] > -180 && bPhs[i] <= -180) { bPcIdx = i; break; }
  }
  const bWpc = bWs[bPcIdx] || 3;
  const bMagAtPc = bMag[bPcIdx] || 0;
  const bGM = -bMagAtPc;

  // Find gain crossover
  let bGcIdx = 0;
  for (let i = 1; i < bMag.length; i++) {
    if (bMag[i - 1] > 0 && bMag[i] <= 0) { bGcIdx = i; break; }
  }
  const bWgc = bWs[bGcIdx] || 2;
  const bPM = 180 + bPhs[bGcIdx];

  // Nyquist scale
  const nOx = 10, nOy = 20, nPW = 390, nPH = 330;
  const nCX = nOx + nPW * 0.42, nCY = nOy + nPH / 2;
  const nSc = 55;
  const snx = re => nCX + re * nSc;
  const sny = im => nCY - im * nSc;

  const nPosPath = nyqPts.map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(im).toFixed(1)}`).join(' ');
  const nNegPath = nyqPts.slice().reverse().map(({ re, im }, i) =>
    `${i === 0 ? 'M' : 'L'}${snx(re).toFixed(1)},${sny(-im).toFixed(1)}`).join(' ');

  // Bode scale
  const bOx = 416, bOy = 20, bPW = 474, bPH = 140;
  const bswx = w  => bOx + 36 + (Math.log10(w / bWmin) / Math.log10(bWmax / bWmin)) * (bPW - 50);
  const bsmy = dB => bOy + bPH - 18 - ((dB + 60) / 100) * (bPH - 28);
  const bspy = ph => bOy + bPH + 18 + (bPH - 18) - ((ph + 270) / 270) * (bPH - 28);

  const bMagPath = bMag.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${bswx(bWs[i]).toFixed(1)},${Math.max(bOy + 4, Math.min(bOy + bPH, bsmy(v))).toFixed(1)}`).join(' ');
  const bPhsPath = bPhs.map((v, i) =>
    `${i === 0 ? 'M' : 'L'}${bswx(bWs[i]).toFixed(1)},${Math.max(bOy + bPH + 20, Math.min(bOy + 2 * bPH + 20, bspy(v))).toFixed(1)}`).join(' ');

  const bwTicks = [0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 40];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Nyquist vs Bode — Same System G(s)=5/[s(s+1)(s+3)], Both Methods</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <Arr id="nvb-blk" color="#aaa"/>
          <Arr id="nvb-grn" color="#27ae60"/>
          <Arr id="nvb-blu" color="#2E75B6"/>
          <clipPath id="nvb-nq"><rect x={nOx + 8} y={nOy + 6} width={nPW - 16} height={nPH - 12}/></clipPath>
          <clipPath id="nvb-bm"><rect x={bOx + 34} y={bOy + 4} width={bPW - 46} height={bPH - 20}/></clipPath>
          <clipPath id="nvb-bp"><rect x={bOx + 34} y={bOy + bPH + 22} width={bPW - 46} height={bPH - 20}/></clipPath>
        </defs>

        {/* ── LEFT: Nyquist ── */}
        <rect x={nOx} y={nOy} width={nPW} height={nPH} rx={5} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={nOx + nPW / 2} y={nOy - 6} textAnchor="middle" fontSize={12} fill="#7B0D0D" fontWeight="700">Nyquist Plot</text>

        {[-5,-4,-3,-2,-1,0].map(v => (
          <g key={v}>
            <line x1={snx(v)} y1={nOy + 6} x2={snx(v)} y2={nOy + nPH - 6} stroke="#f8f8f8" strokeWidth="1"/>
            <text x={snx(v)} y={nCY + 14} textAnchor="middle" fontSize={8} fill="#ccc">{v}</text>
          </g>
        ))}
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={nOx + 8} y1={sny(v)} x2={nOx + nPW - 8} y2={sny(v)} stroke="#f8f8f8" strokeWidth="1"/>
            {v !== 0 && <text x={nCX - 6} y={sny(v) + 3} textAnchor="end" fontSize={8} fill="#ccc">{v > 0 ? `j${v}` : `-j${Math.abs(v)}`}</text>}
          </g>
        ))}
        <line x1={nOx + 8} y1={nCY} x2={nOx + nPW - 6} y2={nCY} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#nvb-blk)"/>
        <line x1={nCX}     y1={nOy + 6} x2={nCX} y2={nOy + nPH - 6} stroke="#bbb" strokeWidth="1.3" markerEnd="url(#nvb-blk)"/>
        <text x={nOx + nPW - 4} y={nCY + 4} fontSize={9} fill="#888">Re</text>
        <text x={nCX + 5}       y={nOy + 12} fontSize={9} fill="#888">Im</text>

        <circle cx={snx(-1)} cy={nCY} r={8} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={snx(-1)} y={nCY + 3} textAnchor="middle" fontSize={9} fill="#fff" fontWeight="700">−1</text>

        <path d={nPosPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#nvb-nq)"/>
        <path d={nNegPath} fill="none" stroke="#c0392b" strokeWidth="1.8" strokeDasharray="6,4" clipPath="url(#nvb-nq)" opacity="0.7"/>

        {/* Phase crossover on Nyquist */}
        {(() => {
          // Find where nyq curve hits negative real axis (Im≈0, Re<0)
          let pcPt = null;
          for (let i = 1; i < nyqPts.length; i++) {
            if (nyqPts[i - 1].im < 0 && nyqPts[i].im >= 0 && nyqPts[i].re < 0) {
              pcPt = nyqPts[i]; break;
            }
          }
          if (!pcPt) return null;
          const GM_dB_n = -20 * Math.log10(Math.abs(pcPt.re));
          return (
            <g>
              <circle cx={snx(pcPt.re)} cy={nCY} r={5} fill="#2E75B6" stroke="#fff" strokeWidth="2"/>
              <line x1={snx(pcPt.re)} y1={nCY} x2={snx(-1)} y2={nCY} stroke="#2E75B6" strokeWidth="2.5" markerStart="url(#nvb-blu)" markerEnd="url(#nvb-blu)"/>
              <text x={(snx(pcPt.re)+snx(-1))/2} y={nCY-10} textAnchor="middle" fontSize={9} fill="#2E75B6" fontWeight="700">
                GM={GM_dB_n.toFixed(1)} dB
              </text>
            </g>
          );
        })()}

        {/* Gain crossover on Nyquist (unit circle intersection) */}
        {(() => {
          let gcPt = null;
          for (let i = 1; i < nyqPts.length; i++) {
            const mag = Math.sqrt(nyqPts[i].re**2+nyqPts[i].im**2);
            const magPrev = Math.sqrt(nyqPts[i-1].re**2+nyqPts[i-1].im**2);
            if (magPrev >= 1 && mag < 1 && nyqPts[i].im < 0) { gcPt = nyqPts[i]; break; }
          }
          if (!gcPt) return null;
          const PMn = Math.atan2(gcPt.im, gcPt.re)*180/Math.PI + 180;
          return (
            <g>
              <circle cx={snx(gcPt.re)} cy={sny(gcPt.im)} r={5} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
              <text x={snx(gcPt.re)+7} y={sny(gcPt.im)-5} fontSize={9} fill="#27ae60" fontWeight="700">PM={PMn.toFixed(1)}°</text>
            </g>
          );
        })()}

        <text x={nOx + nPW/2} y={nOy+nPH-4} textAnchor="middle" fontSize={9} fill="#555">N=0, P=0, Z=0 \u2192 Stable</text>

        {/* ── RIGHT: Bode plots ── */}
        {/* Magnitude */}
        <rect x={bOx} y={bOy} width={bPW} height={bPH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={bOx + bPW/2} y={bOy - 6} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Bode Magnitude [dB]</text>

        {bwTicks.map(wv => (
          <g key={wv}>
            <line x1={bswx(wv)} y1={bOy+4} x2={bswx(wv)} y2={bOy+bPH-16} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={bswx(wv)} y={bOy+bPH-4} textAnchor="middle" fontSize={7.5} fill="#ccc">{wv}</text>
          </g>
        ))}
        {[-60,-40,-20,0,20,40].map(mv => (
          <g key={mv}>
            <line x1={bOx+34} y1={bsmy(mv)} x2={bOx+bPW-14} y2={bsmy(mv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={bOx+30} y={bsmy(mv)+3} textAnchor="end" fontSize={7.5} fill="#ccc">{mv}</text>
          </g>
        ))}
        <line x1={bOx+34} y1={bsmy(0)} x2={bOx+bPW-14} y2={bsmy(0)} stroke="#aaa" strokeWidth="1" strokeDasharray="4,3"/>
        <line x1={bOx+34} y1={bOy+bPH-16} x2={bOx+bPW-8} y2={bOy+bPH-16} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#nvb-blk)"/>
        <line x1={bOx+34} y1={bOy+bPH-16} x2={bOx+34}    y2={bOy+4}      stroke="#aaa" strokeWidth="1.2" markerEnd="url(#nvb-blk)"/>
        <path d={bMagPath} fill="none" stroke="#c0392b" strokeWidth="2.5" clipPath="url(#nvb-bm)"/>

        {/* Mark gain crossover on Bode */}
        <line x1={bswx(bWgc)} y1={bOy+4} x2={bswx(bWgc)} y2={bOy+bPH-16} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
        <circle cx={bswx(bWgc)} cy={bsmy(0)} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
        <text x={bswx(bWgc)+5} y={bsmy(0)-5} fontSize={8.5} fill="#27ae60">ωgc={bWgc.toFixed(1)}</text>

        {/* Mark phase crossover on Bode */}
        {bPcIdx > 0 && (
          <>
            <line x1={bswx(bWpc)} y1={bOy+4} x2={bswx(bWpc)} y2={bOy+bPH-16} stroke="#2E75B6" strokeWidth="1.5" strokeDasharray="4,3"/>
            <circle cx={bswx(bWpc)} cy={bsmy(bMagAtPc)} r={4} fill="#2E75B6" stroke="#fff" strokeWidth="1.5"/>
            <line x1={bswx(bWpc)} y1={bsmy(0)} x2={bswx(bWpc)} y2={bsmy(bMagAtPc)} stroke="#2E75B6" strokeWidth="2.5" markerStart="url(#nvb-blu)" markerEnd="url(#nvb-blu)"/>
            <text x={bswx(bWpc)+5} y={(bsmy(0)+bsmy(bMagAtPc))/2+4} fontSize={8.5} fill="#2E75B6" fontWeight="700">GM={bGM.toFixed(1)}dB</text>
          </>
        )}

        {/* Phase */}
        <rect x={bOx} y={bOy+bPH+18} width={bPW} height={bPH} rx={4} fill="#fff" stroke="#f0cece" strokeWidth="1.5"/>
        <text x={bOx+bPW/2} y={bOy+bPH+10} textAnchor="middle" fontSize={11} fill="#7B0D0D" fontWeight="700">Bode Phase [°]</text>

        {bwTicks.map(wv => (
          <line key={wv} x1={bswx(wv)} y1={bOy+bPH+22} x2={bswx(wv)} y2={bOy+2*bPH+20} stroke="#f5f5f5" strokeWidth="1"/>
        ))}
        {[-270,-180,-90,0].map(pv => (
          <g key={pv}>
            <line x1={bOx+34} y1={bspy(pv)} x2={bOx+bPW-14} y2={bspy(pv)} stroke="#f5f5f5" strokeWidth="1"/>
            <text x={bOx+30} y={bspy(pv)+3} textAnchor="end" fontSize={7.5} fill="#ccc">{pv}°</text>
          </g>
        ))}
        <line x1={bOx+34} y1={bspy(-180)} x2={bOx+bPW-14} y2={bspy(-180)} stroke="#c0392b" strokeWidth="1.2" strokeDasharray="5,3" opacity="0.6"/>
        <line x1={bOx+34} y1={bOy+2*bPH+20} x2={bOx+bPW-8} y2={bOy+2*bPH+20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#nvb-blk)"/>
        <line x1={bOx+34} y1={bOy+bPH+22}    x2={bOx+34}    y2={bOy+2*bPH+20} stroke="#aaa" strokeWidth="1.2" markerEnd="url(#nvb-blk)"/>
        <path d={bPhsPath} fill="none" stroke="#2E75B6" strokeWidth="2.5" clipPath="url(#nvb-bp)"/>

        {/* PM annotation on phase */}
        {bGcIdx > 0 && (
          <>
            <line x1={bswx(bWgc)} y1={bOy+bPH+22} x2={bswx(bWgc)} y2={bOy+2*bPH+20} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,3"/>
            <circle cx={bswx(bWgc)} cy={bspy(bPhs[bGcIdx])} r={4} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
            <line x1={bswx(bWgc)} y1={bspy(-180)} x2={bswx(bWgc)} y2={bspy(bPhs[bGcIdx])} stroke="#27ae60" strokeWidth="2.5" markerStart="url(#nvb-grn)" markerEnd="url(#nvb-grn)"/>
            <text x={bswx(bWgc)+5} y={(bspy(-180)+bspy(bPhs[bGcIdx]))/2+4} fontSize={8.5} fill="#27ae60" fontWeight="700">PM={bPM.toFixed(1)}°</text>
          </>
        )}

        <text x={bOx+38} y={bOy+2*bPH+16} fontSize={8} fill="#aaa">ω [rad/s]</text>
      </svg>
      <div style={S.dCaption}>Both methods extract the same stability margins from G(s)=5/[s(s+1)(s+3)]: GM≈{bGM.toFixed(0)} dB (gain can be multiplied by ~{(Math.pow(10,bGM/20)).toFixed(1)} before instability) and PM≈{bPM.toFixed(0)}°. The Nyquist plot shows these geometrically — GM as the distance from the real-axis crossing to −1, PM as the angle at the unit-circle crossing. The Bode plot shows the same information as the gap to 0 dB at the phase crossover and the gap to −180° at the gain crossover.</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main Export
// ─────────────────────────────────────────────────────────────────
export default function NyquistCriterion() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Control Systems — Nyquist Criterion</h1>
      <p style={S.p}>
        The Nyquist stability criterion is the most powerful and general tool in frequency-domain stability analysis. Unlike the Bode criterion — which applies only to minimum-phase, open-loop stable systems — the Nyquist criterion handles plants with open-loop unstable poles, non-minimum phase zeros, time delays, and any other pathological behaviour. It is rooted in Cauchy's argument principle from complex analysis: as the complex variable s traverses a closed contour encircling the right half-plane (the Nyquist D-contour), the image G(s) in the complex plane encircles the critical point −1+j0 a number of times equal to the difference Z−P, where Z is the number of unstable closed-loop poles and P is the number of unstable open-loop poles. Stability requires Z=0, so the criterion becomes: the number of counterclockwise encirclements of −1 by the Nyquist plot of G(jω) must equal P.
      </p>

      <InfoBox title="Core Concepts at a Glance" items={[
        "Nyquist plot: polar plot of G(jω) as ω sweeps from −∞ to +∞ (or 0⁺ to +∞ plus its mirror)",
        "Critical point: −1+j0 — if the Nyquist curve passes through it, the system is marginally stable",
        "Cauchy argument principle: N = Z − P, where N = CW encirclements of −1",
        "Stability criterion: Z = 0 requires N = −P (i.e. P counterclockwise encirclements)",
        "Minimum-phase, stable open-loop (P=0): stable iff N = 0 (no encirclements of −1)",
        "Gain margin: 1/|G(jωpc)| where G crosses the negative real axis (phase crossover)",
        "Phase margin: angle above −180° at the unit-circle crossing (gain crossover)",
        "Advantages over Bode: handles RHP poles, non-minimum phase, and delay systems",
      ]}/>

      <h2 style={S.h2}>The Nyquist Contour (D-Contour)</h2>
      <p style={S.p}>
        The Nyquist criterion is built on Cauchy's argument principle, which requires a closed contour in the s-plane that encircles the region of interest — the right half-plane. The standard Nyquist contour (D-contour) consists of: the positive imaginary axis from j0⁺ to j∞ (segment A), a large clockwise semicircle of radius R→∞ (segment B), the negative imaginary axis from −j∞ to −j0⁻ (segment C), and — if the open-loop transfer function has poles on the imaginary axis — a small clockwise indentation of radius ε→0 around each such pole (segment D for a pole at the origin).
      </p>
      <DiagramNyquistContour/>
      <Table2 rows={[
        ["Cauchy argument principle","If G(s) is analytic inside and on a closed contour C except at poles,\nand C encircles Z zeros and P poles of F(s), then\nthe image G(C) encircles the origin N = Z−P times clockwise"],
        ["Application to CL stability","F(s) = 1 + G(s)H(s) — closed-loop characteristic polynomial\nZeros of F(s) = closed-loop poles  |  Poles of F(s) = open-loop poles\nWant: all zeros of F in LHP → Z = 0"],
        ["Shift to −1+j0","Since F(s) = 1+GH, G(s) encircling −1 = F(s) encircling 0\nCount encirclements of −1 by G(jω) instead of origin by F(jω)\nEquivalent mathematical statement — more convenient visually"],
        ["D-contour segments","A: s = jω, ω: 0⁺ to +∞  →  upper half Nyquist plot\nB: s = Re^{jθ}, R→∞  →  typically maps to origin (proper G)\nC: s = jω, ω: −∞ to 0⁻  →  lower half = complex conjugate of A\nD: s = εe^{jθ} around imaginary poles, ε→0"],
        ["Integrator indentation","G(s) = K/(s·...) has a pole at s=0 on the D-contour\nIndent CW around s=0: s = εe^{jθ}, θ: +π/2 to −π/2\nG(εe^{jθ}) = (K/ε)e^{−jθ} → semicircle of radius ∞ CW in G-plane\nConnect upper and lower Nyquist plots with this infinite-radius arc"],
        ["Proper vs improper G","Proper (n_poles ≥ n_zeros): G(Re^{jθ})→0 as R→∞ → maps to origin\nStrictly proper: G(j∞)=0 → closed curve at origin\nIf G is improper: segment B maps to ∞ — must track more carefully"],
        ["P = open-loop RHP poles","Count ALL poles of G(s)H(s) with Re(s) > 0\nP = 0 for stable, minimum-phase plants\nNot to be confused with imaginary-axis poles (on boundary, handled by indent.)"],
        ["Practical shortcut","For minimum-phase, stable OL: only need positive ω plot\nNegative ω is mirror image — just check N for positive ω upper plot\nFor P≠0: need full Nyquist including indentation arc"],
      ]}/>

      <h2 style={S.h2}>Nyquist Plot Construction</h2>
      <p style={S.p}>
        The Nyquist plot is constructed by evaluating G(jω) for ω from 0 to ∞ and plotting the resulting complex numbers as a curve in the Re-Im plane. The negative frequency portion (ω from −∞ to 0) is simply the complex conjugate, reflected about the real axis. Key features — the DC value G(0), the behaviour as ω→∞, any real-axis crossings, and the unit-circle intersections — determine the stability margins.
      </p>
      <DiagramNyquistConstruction/>
      <Table2 rows={[
        ["G(jω) evaluation","Substitute s = jω into G(s), separate real and imaginary parts\nG(jω) = Re{G(jω)} + j·Im{G(jω)}\nPlot (Re, Im) as a curve parameterized by ω"],
        ["DC value G(0)","For Type 0: G(0) = K/(product of real poles) — finite positive real\nFor Type 1: G(0) → ±∞ (pole at origin — indentation maps to ∞)\nStart of Nyquist plot at ω→0⁺"],
        ["High-frequency behaviour","For strictly proper G: G(j∞) = 0 → curve approaches origin\nAngle of approach: −90·(n_poles − n_zeros) degrees\nSets the direction from which the curve arrives at origin"],
        ["Real-axis crossings","Im{G(jω)} = 0 → solve for ω\nThese ω values give the real-axis crossing points\nThe value Re{G(jωcross)} determines if −1 is encircled"],
        ["Unit circle intersections","|G(jω)| = 1 → gain crossover frequency ωgc\nPhase at ωgc → phase margin PM = 180° + ∠G(jωgc)"],
        ["Mirror symmetry","G(j(−ω)) = G*(jω) — complex conjugate symmetry\nLower half of Nyquist = reflection of upper half about real axis\nOnly need to compute ω > 0, then mirror"],
        ["Multiplying by gain K","Scaling: (KG)(jω) = K·G(jω) — scales the entire curve radially\nThe curve passes through −1 when K = 1/|G(jωpc)|"],
        ["Reading the Nyquist plot","Start: DC value on positive real axis (Type 0)\nFinish: origin from angle −90°·(poles−zeros)\nCheck: does curve pass to the left or right of −1?"],
      ]}/>

      <h2 style={S.h2}>Counting Encirclements</h2>
      <p style={S.p}>
        The central operation of the Nyquist criterion is counting how many times the Nyquist curve winds around the critical point −1+j0. A clockwise winding contributes +1 to N, and a counterclockwise winding contributes −1. The ray method provides a mechanical procedure: draw a ray from −1 to the right, count signed crossings of the Nyquist curve with this ray.
      </p>
      <DiagramEncirclement/>
      <Table2 rows={[
        ["Definition of encirclement","The Nyquist curve makes N complete clockwise loops around −1\nN > 0: N clockwise encirclements\nN < 0: |N| counterclockwise encirclements\nN = 0: curve does not encircle −1 (may still pass nearby)"],
        ["Ray counting method","Draw ray from −1 in any direction (usually to the right)\nCount: CW crossings (downward) add 1, CCW crossings (upward) subtract 1\nN = algebraic sum of signed crossings"],
        ["Stability formula","N = Z − P  (Cauchy's argument principle)\nZ = N + P  (closed-loop RHP poles = encirclements + open-loop RHP poles)\nStable ↔ Z = 0 ↔ N = −P"],
        ["Minimum-phase (P=0)","Stable ↔ N = 0 (no encirclements of −1)\nSimplest case — most industrial systems\nEquivalent to Bode stability criterion (GM>0, PM>0)"],
        ["One RHP open-loop pole (P=1)","Need N = −1 (one CCW encirclement) for stability\nThe Nyquist curve must wind CCW around −1 exactly once\nThis is how feedback can stabilize an unstable plant"],
        ["Partial encirclements","If the Nyquist curve passes through −1: marginally stable\nZ is not well-defined (closed-loop pole on jω axis)\nThis is the sustained oscillation / marginal stability case"],
        ["Closed vs open curve","For proper G(s): Nyquist curve is closed (returns to origin)\nFor improper G(s): need to track the curve from ∞ to ∞\nIntegrators: the indentation maps to a large arc connecting the two halves"],
        ["Practical encirclement check","For simple Type 0/1 systems: just check real-axis crossing vs −1\nCrossing to right of −1: N=0 (stable if P=0)\nCrossing to left of −1: N=+2 (each full wrap) → unstable if P=0"],
      ]}/>

      <h2 style={S.h2}>Stability Examples</h2>
      <p style={S.p}>
        For the canonical three-pole system G(s)=K/[s(s+1)(s+2)], stability depends entirely on where the Nyquist curve crosses the negative real axis. This crossing occurs at a fixed frequency ω=√2 rad/s, with a value that is proportional to gain K. The ultimate (critical) gain Ku=6 marks the exact transition between stable and unstable behaviour.
      </p>
      <DiagramStabilityExamples/>
      <Table2 rows={[
        ["Ultimate gain Ku","Ku = gain K at which Nyquist curve passes through −1\nEquivalent to: K at which Bode phase = −180° when |G|=1\nFor G=K/[s(s+1)(s+2)]: phase = −180° at ω=√2, |G(j√2)|=1/6 → Ku=6"],
        ["Type 1 negative real crossing","For Type 1 (integrator) plant: upper Nyquist curve arrives from +∞\nCurve traces through 2nd and 3rd quadrants\nCrossing of negative real axis at Im=0, Re<0 determines stability"],
        ["K < Ku: stable","Real-axis crossing value > −1 (closer to origin)\nCurve does not encircle −1: N=0, Z=0, P=0 → stable"],
        ["K = Ku: marginally stable","Curve passes exactly through −1\nClosed-loop pole on imaginary axis → sustained oscillation\nFrequency of oscillation = ωpc = √2 rad/s"],
        ["K > Ku: unstable","Curve crosses real axis at a value less than −1\nFor Type 1 with 3 poles: curve encircles −1 TWICE clockwise\nZ=2 unstable closed-loop poles"],
        ["Why 2 encirclements for Type 1","Upper curve arrives from −∞ along real axis (integrator)\nAs K↑ past Ku, two separate real-axis crossings (both < −1)\nEach crossing pair creates one complete encirclement"],
        ["Gain margin formula","GM = 1/|G(jωpc)| where ωpc is phase crossover frequency\nFor G=K/[s(s+1)(s+2)]: |G(j√2)| = K/6, so GM = 6/K\nAt K=1: GM = 6 = 15.6 dB"],
        ["Robustness interpretation","GM and PM quantify distance from instability\nSmall GM: gain increase of factor GM causes oscillation\nSmall PM: phase addition of PM° causes oscillation"],
      ]}/>

      <h2 style={S.h2}>Gain Margin &amp; Phase Margin from Nyquist</h2>
      <p style={S.p}>
        The Nyquist plot provides a direct geometric picture of both stability margins simultaneously. The gain margin is the factor by which the Nyquist curve must be scaled outward for it to just reach the critical point — read as the reciprocal of the distance from the phase crossover point to the origin. The phase margin is the angle between the gain crossover point (on the unit circle) and the −180° direction.
      </p>
      <DiagramNyquistMargins/>
      <Table2 rows={[
        ["Phase crossover on Nyquist","Point where Nyquist curve intersects the negative real axis\nFrequency: ωpc where Im{G(jω)} = 0 and Re{G(jω)} < 0\nGM = 1/|Re{G(jωpc)}| — how far from −1 is this crossing"],
        ["Gain crossover on Nyquist","Point where Nyquist curve intersects the unit circle (|G|=1)\nFrequency: ωgc where |G(jωgc)| = 1\nPM = 180° + ∠G(jωgc) = angle above −180° direction"],
        ["Geometric GM interpretation","GM = distance from origin to PC point, inverted\nIf PC is at Re = −0.33: GM = 1/0.33 = 3× = 9.5 dB\nLarger GM: PC closer to origin → more margin before reaching −1"],
        ["Geometric PM interpretation","Unit circle touches Nyquist curve at GC\nAngle from negative real axis (−1 direction) to GC = PM\nLarger PM: GC further from the negative real axis"],
        ["Simultaneous reading","Nyquist shows both margins on one plot — natural representation\nBode needs two separate plots to extract both margins\nFor complex systems, Nyquist can show multiple crossings"],
        ["Multiple crossings","Some systems have multiple phase crossovers (Nyquist crosses real axis >once)\nMust check all crossings and count total encirclements\nConditionally stable systems: stable only for a range of K"],
        ["Conditionally stable systems","Gain margin on both sides: stable for K₁ < K < K₂\nNyquist crosses real axis twice on left of −1 with opposite signs\nExample: some plants with RHP zeros or resonances"],
        ["Relationship to step response","PM ≈ 100ζ° (approximate for 0 < ζ < 0.7)\nGM > 6 dB: gain can double; corresponds roughly to ζ > 0.3\nGood target: PM=45°–60°, GM=6–12 dB for well-damped response"],
      ]}/>

      <h2 style={S.h2}>Non-Minimum Phase &amp; RHP Open-Loop Poles</h2>
      <p style={S.p}>
        The true power of the Nyquist criterion emerges for systems that cannot be analysed with Bode. Non-minimum phase plants (with RHP zeros) have more phase lag than their magnitude response would suggest — the Bode criterion incorrectly predicts stability. Plants with open-loop RHP poles require the Nyquist curve to make a precise number of counterclockwise encirclements to achieve stability. In both cases, only the Nyquist criterion gives the correct answer.
      </p>
      <DiagramNonMinimumPhase/>
      <Table2 rows={[
        ["Minimum phase definition","A system is minimum-phase if it has no RHP zeros and no time delay\nMinimum-phase: phase is uniquely determined by the magnitude (Bode)\nNon-minimum phase: extra phase lag beyond the minimum possible"],
        ["RHP zero effect on Nyquist","RHP zero s=a gives factor (s−a): at s=jω → (jω−a)\nPhase: ∠(jω−a) = π − arctan(ω/a) → adds phase lag beyond 90°\nNyquist curve wraps more than equivalent minimum-phase system"],
        ["Bode criterion failure","Bode: 'stable if gain is 0 dB before phase reaches −180°'\nFor non-minimum-phase: phase can reach −180° while gain>0 dB\n→ Bode declares stable when system may actually be unstable"],
        ["RHP zero and bandwidth","RHP zero at ω=a limits closed-loop bandwidth to ≲a/2 rad/s\nAttempting higher bandwidth: phase crosses −180° → instability\nFundamental limitation — cannot be compensated away"],
        ["RHP open-loop pole (P=1)","Open-loop unstable plant: P=1 → need N=−1 for stability\nNyquist curve must encircle −1 once CCW\nRequires feedback strong enough to overcome instability"],
        ["Stabilizing an unstable plant","Example: G(s)=K/((s−1)(s+2))\nFor stability with P=1: need exactly 1 CCW encirclement\nToo little gain: N=0 → Z=1 unstable\nToo much gain: N=+1 (CW) → Z=2 unstable\nCorrect gain range: N=−1 (CCW) → Z=0 stable"],
        ["Double RHP pole (P=2)","Need N=−2: curve encircles −1 twice CCW\nTypically requires complex controller structure\nExample: double integrator with unstable resonance"],
        ["Non-minimum phase + RHP pole","Most challenging case: P>0 AND RHP zero\nBode gives no useful information\nNyquist gives the exact encirclement requirement: N=−P"],
      ]}/>

      <h2 style={S.h2}>Dead Time and the Nyquist Plot</h2>
      <p style={S.p}>
        Time delay e^(−Ls) is non-minimum phase — it has infinite phase lag and no zeros to cancel poles. In the s-domain it is not a rational function, so root locus and standard factored transfer function analysis do not directly apply. The Nyquist plot handles dead time naturally: the magnitude of e^(−jωL) is always 1 (no effect on the spiral shape), but the phase e^(−jωL) = cos(ωL)−j·sin(ωL) rotates the curve by −ωL radians at each frequency — causing the Nyquist spiral to wind more tightly and potentially encircle −1.
      </p>
      <DiagramDeadTime/>
      <Table2 rows={[
        ["Dead time transfer function","e^{−Ls}: Laplace transform of a pure delay of L seconds\n|e^{−jωL}| = 1 at all frequencies — no magnitude effect\n∠e^{−jωL} = −Lω radians = −Lω·(180/π) degrees"],
        ["Effect on Nyquist curve","Multiplies G(jω) by e^{−jωL}: rotates each point by −Lω rad\nMagnitude unchanged — same distance from origin at each ω\nPhase adds −Lω: curve spirals as ω increases (constant rotation per unit ω)"],
        ["Bandwidth limitation","Instability when phase reaches −180° before gain drops below 1\nApproximate: ωgc·L < PM (in radians) for stability\nMaximum bandwidth: ωgc ≲ PM_rad / L = PM°·π / (180·L)"],
        ["Pade approximation","First-order: e^{−Ls} ≈ (1−Ls/2)/(1+Ls/2) — rational approximation\nAllows standard Nyquist/Bode analysis\nAccurate for ωL < 1 but becomes inaccurate for large ωL"],
        ["Smith predictor","Control structure that removes dead time from the control loop\nPredicted output y* = G(s)·u(t) added to estimate current state\nAllows controller to be designed as if there is no delay"],
        ["Critical gain with dead time","Ku decreases as L increases\nFor G=K/((s+1)(0.5s+1)): Ku = function of L\nAs L→∞: Ku→0 — eventually any gain causes instability"],
        ["Frequency domain test","Apply sinusoidal input at ωgc: output lags by PM + Lωgc·(180/π) degrees\nIf total lag > 180°: unstable\nDead time L adds Lωgc·(180/π)° additional lag at gain crossover"],
        ["Industrial importance","Dead time arises from: transport delays in pipes/conveyors, computation lag,\nsampling in digital control, sensor transmission delays\nMost chemical/process plants have significant dead time\nNyquist plot immediately shows whether current gain is safe"],
      ]}/>

      <h2 style={S.h2}>Nyquist vs Bode Comparison</h2>
      <p style={S.p}>
        The Nyquist and Bode plots carry the same frequency-response information but present it in complementary ways. The Bode plot uses two separate graphs on log-frequency axes, making it easy to read margins numerically and to construct asymptotic approximations. The Nyquist plot uses a single polar curve, making encirclements visually obvious and directly applicable to unstable plants. For minimum-phase stable plants they give identical stability verdicts; the Nyquist criterion is strictly more powerful for non-minimum-phase and unstable open-loop cases.
      </p>
      <DiagramNyquistVsBode/>
      <Table2 rows={[
        ["Information content","Both show: |G(jω)| and ∠G(jω) for all ω > 0\nBode: two separate plots vs log(ω) axis\nNyquist: single polar plot, ω implicit along the curve"],
        ["Gain margin comparison","Bode: GM = −|G(jωpc)| in dB (vertical distance to 0 dB at ωpc)\nNyquist: GM = 1/|G(jωpc)| as a gain ratio (horizontal distance from PC to −1)\nBoth give the same numerical answer"],
        ["Phase margin comparison","Bode: PM = 180° + phase at 0 dB crossing\nNyquist: PM = angle of unit-circle intersection from −180° direction\nBode reads PM directly as degrees; Nyquist reads it as a geometric angle"],
        ["Bode advantage","Easy to construct asymptotic approximations\nLog-freq scale shows wide range simultaneously\nSlope ↔ pole/zero count immediately visible\nFaster for design (loop shaping)"],
        ["Nyquist advantage","Handles non-minimum phase and RHP pole systems correctly\nSingle plot shows entire stability picture\nConditionally stable systems visible immediately\nHandles systems where Bode criterion breaks down"],
        ["When to use Bode","Plant is minimum-phase and open-loop stable (P=0)\nQuick design and tuning via loop shaping\nAsymptotic analysis and hand computation"],
        ["When to use Nyquist","Plant has RHP zeros or open-loop RHP poles\nSystem has significant dead time\nNeed to handle conditional stability\nVerifying stability of unusual feedback configurations"],
        ["Equivalent statements","Bode stable: GM>0 dB and PM>0° (for minimum-phase, P=0)\nNyquist: N=0 (no encirclements of −1) when P=0\nBoth necessary AND sufficient for the same class of plants"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Nyquist criterion: N = Z − P,  where N = CW encirclements of −1, Z = CL RHP poles, P = OL RHP poles",
        "Stability: Z = 0  ↔  N = −P  (counterclockwise encirclements equal number of OL unstable poles)",
        "Minimum phase (P=0): stable ↔ N = 0 (no encirclement of −1+j0)",
        "Gain margin: GM = 1/|G(jωpc)| where Im{G(jωpc)}=0, Re{G(jωpc)}<0",
        "Phase margin: PM = 180° + ∠G(jωgc) where |G(jωgc)|=1",
        "Dead time effect: rotates Nyquist curve by −Lω rad at each frequency; magnitude unchanged",
        "Bandwidth limit with delay: ωgc · L ≤ PM (radians) ≈ PM(°) · π/180",
        "Pade approx: e^{−Ls} ≈ (1−Ls/2)/(1+Ls/2)  (rational approximation for small ωL)",
      ]}/>
    </div>
  );
}